import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import StatusBadge from './StatusBadge';

const ClientDetailModal = ({ client, onClose, onStatusUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [notes, setNotes] = useState(client?.notes || []);
  const [interactionHistory] = useState(client?.interactionHistory || []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' }

  ];

  const formatDateTime = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const note = {
      id: Date.now(),
      author: 'You',
      content: newNote,
      timestamp: new Date().toISOString()
    };

    setNotes([...notes, note]);
    setNewNote('');
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 md:p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col relative">

        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Icon name="User" size={20} color="white" />
              </div>
              <div className="flex flex-col">
                <h2 className="text-base md:text-xl font-semibold truncate">{client?.name}</h2>
                <p className="text-xs md:text-sm text-white/80 break-all">Client ID: {client?.id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 justify-between sm:justify-end">
              <StatusBadge status={client?.status} />
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={onClose}
                className="text-white hover:bg-white/20"
              />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-white">
          <nav className="flex overflow-x-auto px-2 sm:px-4 hide-scrollbar text-sm">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
              flex items-center space-x-2 py-2 px-3 sm:px-4 border-b-2 font-medium whitespace-nowrap transition-all
              ${activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                  }
            `}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Modal Content */}
        <div className="p-4 md:p-6 overflow-y-auto flex-1 text-sm md:text-base">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* Contact Information */}
              <div className="space-y-3">
                <h3 className="text-base md:text-lg font-medium text-gray-900">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
                    <Icon name="Mail" size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm text-gray-900 break-all">{client?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
                    <Icon name="Phone" size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm text-gray-900">{client?.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
                    <Icon name="MapPin" size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-sm text-gray-900">{client?.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
                    <Icon name="Globe" size={16} className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">IP Address</p>
                      <p className="text-sm text-gray-900">{client?.ipAddress}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div className="space-y-3">
                <h3 className="text-base md:text-lg font-medium text-gray-900">Service Details</h3>
                <div className="space-y-2">
                  <div className="p-2 rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-500">Service Interest</p>
                    <p className="text-sm text-gray-900">{client?.serviceInterest}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-500">Inquiry Date</p>
                    <p className="text-sm text-gray-900">{formatDateTime(client?.inquiryDate)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-500">Last Interaction</p>
                    <p className="text-sm text-gray-900">{formatDateTime(client?.lastInteraction)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50">
                    <p className="text-xs text-gray-500">Budget Range</p>
                    <p className="text-sm text-gray-900">{client?.budgetRange || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Message */}
              {client?.message && (
                <div className="lg:col-span-2">
                  <h3 className="text-base md:text-lg font-medium text-gray-900">Initial Message</h3>
                  <div className="bg-blue-50 rounded-lg p-3 md:p-4 border border-blue-100">
                    <p className="text-sm md:text-base text-gray-900 leading-relaxed break-words">{client?.message}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-base md:text-lg font-medium text-gray-900">Interaction History</h3>
              {/* history content */}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <h3 className="text-base md:text-lg font-medium text-gray-900">Notes</h3>
              {/* notes content */}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between p-4 md:p-6 border-t border-gray-200 gap-2">
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Button
              variant="outline"
              size="sm"
              // iconName="Mail"
              iconPosition="left"
              className="flex-1 sm:flex-none rounded-xl py-2 px-4 shadow-sm hover:shadow-md transition-all"
            >
              📧 Send Email
            </Button>
            <Button
              variant="outline"
              size="sm"
              // iconName="Phone"
              iconPosition="left"
              className="flex-1 sm:flex-none rounded-xl py-2 px-4 shadow-sm hover:shadow-md transition-all"
            >
              📞 Schedule Call
            </Button>
          </div>

          <div className="w-full md:w-auto">
            <Button
              onClick={onClose}
              variant="primary"
              iconName="X"
              iconPosition="left"
              className="w-full md:w-auto bg-black hover:bg-gray-800 text-white"
            >
              Close
            </Button>

          </div>
        </div>
      </div>

      <style jsx>{`
    .hide-scrollbar {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
  `}</style>
    </div>


  );
};

export default ClientDetailModal;